import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API_URL } from '../config/api';

// Wraps the matched part of `text` in <mark> so people see why a row matched.
const highlight = (text, query) => {
  const q = query.trim();
  const at = q ? String(text).indexOf(q) : -1;
  if (at < 0) return text;
  return (
    <>
      {text.slice(0, at)}<mark>{text.slice(at, at + q.length)}</mark>{text.slice(at + q.length)}
    </>
  );
};

/**
 * One search box for ตำบล / อำเภอ / จังหวัด / รหัสไปรษณีย์. Type any of them and
 * the list shows complete addresses; picking one hands back all four values.
 */
const ThaiAddressSearch = ({ value, onSelect, disabled, hasError }) => {
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const wrapRef = useRef(null);

  useEffect(() => {
    const close = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) { setOpen(false); setQuery(''); } };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  useEffect(() => {
    const term = query.trim();
    if (term.length < 2) { setOptions([]); return undefined; }
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const resp = await axios.get(`${API_URL}/thai-address/search`, { params: { q: term }, signal: controller.signal });
        setOptions(Array.isArray(resp.data?.results) ? resp.data.results : []);
        setActive(0);
      } catch (err) {
        if (err.name !== 'CanceledError') setOptions([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => { controller.abort(); clearTimeout(timer); };
  }, [query]);

  const current = [value?.subdistrict, value?.district, value?.province].filter(Boolean).join(' › ');
  const shown = open ? query : current;

  const choose = (addr) => {
    onSelect(addr);
    setOpen(false);
    setQuery('');
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive(i => Math.min(i + 1, options.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(i => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter' && open && options[active]) { e.preventDefault(); choose(options[active]); }
    else if (e.key === 'Escape') { setOpen(false); setQuery(''); }
  };

  const term = query.trim();
  return (
    <div className="rs-combo" ref={wrapRef}>
      <div className="rs-search">
        <i className="bi bi-search"></i>
        <input
          type="text"
          role="combobox"
          aria-expanded={open}
          autoComplete="off"
          name="address_search"
          value={shown}
          disabled={disabled}
          placeholder="พิมพ์ชื่อตำบล อำเภอ จังหวัด หรือรหัสไปรษณีย์..."
          className={`rs-input${hasError ? ' rs-input--error' : ''}`}
          onFocus={(e) => { setOpen(true); e.target.select(); }}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onKeyDown={onKeyDown}
        />
      </div>
      {open && !disabled && term.length >= 2 && (
        <div className="rs-options rs-combo__list" role="listbox">
          {loading && options.length === 0 && <div className="rs-options__empty">กำลังค้นหา...</div>}
          {!loading && options.length === 0 && <div className="rs-options__empty">ไม่พบที่อยู่ที่ตรงกับ "{term}"</div>}
          {options.map((o, i) => (
            <button
              type="button"
              role="option"
              aria-selected={i === active}
              key={`${o.province}-${o.district}-${o.subdistrict}-${o.zipcode}`}
              className={`rs-option${i === active ? ' is-active' : ''}`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => choose(o)}
            >
              <span className="rs-option__title">
                ตำบล/แขวง {highlight(o.subdistrict, term)} · อำเภอ/เขต {highlight(o.district, term)}
              </span>
              <span className="rs-option__meta">จังหวัด {highlight(o.province, term)} · {highlight(String(o.zipcode || ''), term)}</span>
            </button>
          ))}
        </div>
      )}
      {open && term.length < 2 && (
        <div className="rs-help" style={{ marginTop: '6px' }}>พิมพ์อย่างน้อย 2 ตัวอักษร เช่น "บางยี่รงค์" หรือ "75120"</div>
      )}
    </div>
  );
};

export default ThaiAddressSearch;
